import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MenuController } from '@ionic/angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { modeNev } from '../mode-nevek';
import { DataService } from '../data.service';

// Az alrendszer KEZDŐLAPJA. Modulváltás (üzemmód-váltás) után ide navigálunk, hogy NE az előző
// modul oldala maradjon a képernyőn.
//
// Korábban ez egy üres átmeneti lap volt („válassz a bal oldali menüből”), egy nem működő
// „Menü megnyitása” gombbal – vagyis pont ott nem adott semmit, ahol a felhasználó megérkezik.
// Most az adott alrendszer SAJÁT menüjéből épít csempéket, tehát tényleges főmenü.
//
// A csempék forrása KIZÁRÓLAG az aktuális alrendszer menüje lehet. Ez nem magától értetődő: a
// tárolóban lévő kiszámolt menü korábban nem hordozta, melyik modulhoz tartozik, ezért a FITNESS
// kezdőlapon a SULINFORM menüpontjai (Napló, Tanügy, Mulasztások…) jelentek meg. Ezért:
//
//   1. a tárolt listát csak akkor használjuk, ha a MÓDJA egyezik az aktuálissal,
//   2. ha nem egyezik (vagy még nincs kész), magunk kérjük le a mód saját menüjét az API-ból –
//      a backend a menüsorokat alrendszerre szűri, tehát ez mindig a helyes lista,
//   3. és feliratkozunk, hogy amikor az app.component elkészül a teljes listával (kódból
//      injektált Admin gomb, onactive-rejtés, almenük), a csempék frissüljenek.
@Component({
  selector: 'app-valaszto',
  templateUrl: './valaszto.component.html',
  styleUrls: ['./valaszto.component.scss'],
})
export class ValasztoComponent implements OnInit, OnDestroy {
  mod = '';
  modNev = '';
  nev = '';
  intezmeny = '';
  csempek: any[] = [];
  betolt = true;

  /**
   * §12.6 FEJLESZTŐI HOZZÁFÉRÉS A KEZDŐLAPON
   *
   * Ez a lap az ALRENDSZER indítója – nem a betegalkalmazás fel- vagy alsó
   * főmenüje. Az „Admin nincs a beteg főmenüjében" szabály (§1) az app saját
   * navigációjára vonatkozik: az alsó fülsávban és a felső sávban tényleg nincs.
   * Az indítóról viszont ez volt a bejáratott út a konzolhoz, és attól, hogy a
   * csempéket átszerveztük, ezt nem szabad elvenni.
   *
   * Ezért itt is megjelenik – de KÜLÖN, technikai szakaszban, nem az
   * alkalmazás-csempék közé keverve, és új lapot nyitva. A jogosultságot
   * ugyanaz a szerveroldali döntés adja, mint a Profil képernyőnek: egy forrás,
   * két megjelenítés.
   */

  private feliratkozas?: Subscription;

  constructor(
    private data: DataService,
    private http: HttpClient,
    private router: Router,
    private menu: MenuController,
  ) {}

  async ngOnInit() {
    try {
      this.mod = (await this.data.getFromStorage('currentMode')) || '';
      // A Könyvelésnek (NAPLO) nincs bal menüje: a modul saját főmenüje a kezdőlapja.
      if (this.mod === 'NAPLO') { this.router.navigateByUrl('/naplo', { replaceUrl: true }); return; }
      if (this.mod === 'ESZKOZ') { this.router.navigateByUrl('/eszkoz', { replaceUrl: true }); return; }
      if (this.mod === 'SZAMLAZO') { this.router.navigateByUrl('/szamlazo', { replaceUrl: true }); return; }
      if (this.mod === 'INVENTARIO') { this.router.navigateByUrl('/inventario', { replaceUrl: true }); return; }
      if (this.mod === 'GEPJARMU') { this.router.navigateByUrl('/gepjarmu', { replaceUrl: true }); return; }
      // A Bérszámfejtésnek (BER) – mint a Könyvelésnek – a saját főmenüje a kezdőlapja, MINDKÉT témában.
      // (Korábban csak Fókuszban navigált ide; Klasszikusban ezen a semleges csempelapon ragadt, és
      //  nem nyílt meg a bérszámfejtés program. A /ber a /ber/fomenu-ra visz, a keret a témát úgyis hozza.)
      if (this.mod === 'BER') { this.router.navigateByUrl('/ber', { replaceUrl: true }); return; }
      this.modNev = this.mod ? modeNev(this.mod) : '';
      const um: any = await this.data.getFromStorage('userMetadata');
      this.nev = [um?.viselt_vez, um?.viselt_ker].filter(Boolean).join(' ').trim();
      const si: any = await this.data.getFromStorage('sajatIntezmeny');
      this.intezmeny = si?.intezmenyName || '';

      // A bal menü kiszámolt listája – de csak akkor, ha az AKTUÁLIS alrendszeré.
      const tarolt = await this.data.lathatoMenu(this.mod);
      this.csempek = this.csempekbol(tarolt || (await this.modMenujeApibol()));

      // Az app.component menüépítése aszinkron, és lefuthat ez után is. Amikor elkészül,
      // vegyük át tőle a listát – így a kódból injektált pontok (pl. Admin) is megjelennek.
      this.feliratkozas = this.data.lathatoMenu$.subscribe((cs) => {
        if (cs && cs.mod === this.mod) this.csempek = this.csempekbol(cs.elemek);
      });

      // A FITNESS FEJLESZTŐI KONZOLJA ITT MÁR NINCS.
      //
      // A fitness külön értékesített programmá vált (`fitness-app/`), és
      // vele ment a devcenter is – az az ő admin- és validációs
      // konzolja. Az a néhány sor, ami itt a hozzáférést kérdezte,
      // ezért kiesett: a keretrendszer nem hirdethet olyan felületet,
      // amit nem ő szolgál ki.
    } catch {
      this.csempek = [];
    }
    this.betolt = false;
  }

  // Az `adminNyit()` is kikerült: a fitness fejlesztői konzolját nyitotta új
  // lapon, az pedig a különvált programmal együtt költözött. Az utolsó hívója a
  // fenti sablonrészlettel együtt szűnt meg – egy hívatlan metódus a következő
  // olvasónak azt sugallná, hogy van még ilyen belépő.

  ngOnDestroy() { this.feliratkozas?.unsubscribe(); }

  /** Az aktuális mód menüje közvetlenül az API-ból, a bal menüvel azonos szabályok szerint. */
  private async modMenujeApibol(): Promise<any[]> {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    const jogkor = await this.data.getJogkor();
    if (!this.mod || !jogkor || !si?.intezmenyId) return [];
    const sorok: any = await firstValueFrom(
      this.http.get(`/api/menu/${this.mod}/${jogkor}/${si.intezmenyId}`),
    );
    if (!Array.isArray(sorok)) return [];

    // A feltételes (onactive) pontokat ugyanúgy rejtjük, ahogy a bal menü teszi.
    const kapcsolo = new Map<string, boolean>();
    const lathato: any[] = [];
    for (const s of sorok) {
      if (s?.onactive) {
        if (!kapcsolo.has(s.onactive)) {
          kapcsolo.set(s.onactive, !!(await this.data.getFromStorage(s.onactive)));
        }
        if (!kapcsolo.get(s.onactive)) continue;
      }
      lathato.push(s);
    }
    // Almenü-behúzás: a `main` a szülő azonosítója. A gyerek nem külön csempe, a szülőn látszik.
    for (const gyerek of lathato) {
      if (!gyerek.main) continue;
      const szulo = lathato.find((x) => x.id === gyerek.main);
      if (!szulo) continue;
      if (!Array.isArray(szulo.submenu)) szulo.submenu = [];
      szulo.submenu.push(gyerek);
    }
    return lathato;
  }

  /**
   * A külső link és a letiltott pont nem lesz csempe: az egyik kivinné a felhasználót az appból,
   * a másik úgyse nyílna meg. A `main`-nel jelölt sor almenü-gyerek: azt a szülő csempéjén jelezzük.
   */
  private csempekbol(menu: any[]): any[] {
    return (Array.isArray(menu) ? menu : [])
      .filter((m) => m && m.url && !m.external && !m.disabled && !m.main);
  }

  /** Napszak szerinti köszöntés – apróság, de ettől megérkezés lesz, nem üres lap. */
  get koszones(): string {
    const o = new Date().getHours();
    if (o < 5) return 'Jó éjszakát';
    if (o < 10) return 'Jó reggelt';
    if (o < 18) return 'Jó napot';
    return 'Jó estét';
  }

  nyit(cs: any) {
    if (cs?.url) this.router.navigateByUrl(cs.url);
  }

  /** Ha az alrendszerhez egyetlen menüpont sincs, legalább a menü nyíljon ki – működően. */
  menuNyit() { this.menu.open(); }
}
